"use strict";
/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.OK = OK;
exports.Created = Created;
exports.Accepted = Accepted;
exports.NoContent = NoContent;
exports.BadRequest = BadRequest;
exports.Unauthorized = Unauthorized;
exports.Forbidden = Forbidden;
exports.NotFound = NotFound;
exports.Conflict = Conflict;
exports.InternalError = InternalError;
exports.Unavailable = Unavailable;
exports.LimitExceeded = LimitExceeded;
exports.GenericError = GenericError;
exports.GenericErrorAsObject = GenericErrorAsObject;
exports.FileStreamResponse = FileStreamResponse;
exports.isHttpResponse = isHttpResponse;
/**
 * Set OK response
 * @param data Response object in case of a successful response
 */
function OK(ctx, data) {
    ctx.status = 200;
    ctx.set('Content-type', 'application/json; charset=UTF-8;');
    ctx.body = data;
}
/**
 * Set Created response
 * @param data Response object for the newly created resource
 */
function Created(ctx, data) {
    ctx.status = 201;
    ctx.set('Content-type', 'application/json; charset=UTF-8;');
    ctx.body = data;
}
/**
 * Set Accepted response (for async operations)
 * @param data Response object acknowledging the request
 */
function Accepted(ctx, data) {
    ctx.status = 202;
    ctx.set('Content-type', 'application/json; charset=UTF-8;');
    ctx.body = data;
}
/**
 * Set No Content response (successful operation with no body)
 */
function NoContent(ctx) {
    ctx.status = 204;
    ctx.body = null;
}
/**
 * Set Bad Request response
 * @param errorInfo List of error messages
 */
function BadRequest(ctx, errorInfo) {
    GenericError(ctx, errorInfo, 400, 'Bad request');
}
/**
 * Set Unauthorized access response
 * @param errorInfo List of error messages
 */
function Unauthorized(ctx, errorInfo) {
    GenericError(ctx, errorInfo, 401, 'Authentication failed');
}
/**
 * Set Forbidden access response
 * @param errorInfo List of error messages
 */
function Forbidden(ctx, errorInfo) {
    GenericError(ctx, errorInfo, 403, 'Access to resource forbidden');
}
/**
 * Set Not found access response
 * @param errorInfo List of error messages
 */
function NotFound(ctx, errorInfo) {
    GenericError(ctx, errorInfo, 404, 'Resource not found');
}
/**
 * Set Conflict response
 * @param errorInfo List of error messages
 */
function Conflict(ctx, errorInfo) {
    GenericError(ctx, errorInfo, 409, 'Resource conflict');
}
/**
 * Set Unavailable access response
 * @param errorInfo List of error messages
 */
function InternalError(ctx, errorInfo) {
    GenericError(ctx, errorInfo, 500, 'Internal server error');
}
/**
 * Set Unavailable access response
 * @param errorInfo List of error messages
 */
function Unavailable(ctx, errorInfo) {
    GenericError(ctx, errorInfo, 503, 'Resource not available');
}
/**
 * Limit exceeded error
 */
function LimitExceeded(ctx, errorInfo) {
    GenericError(ctx, errorInfo, 429, 'Limit exceeded');
}
/**
 * Function to set a generic error
 * @param errorInfo list of messages or a single message
 * @param status HTTP response status code
 * @param title Short title for the message
 */
function GenericError(ctx, errorInfo, status = 400, title = 'Unknown error') {
    const errorBody = { title };
    if (typeof errorInfo === 'string') {
        errorBody.messages = [errorInfo];
    }
    else if (Array.isArray(errorInfo)) {
        errorBody.messages = errorInfo;
    }
    else {
        errorBody.error = errorInfo;
    }
    ctx.status = status;
    ctx.set('Content-type', 'application/json; charset=UTF-8;');
    ctx.body = errorBody;
}
/**
 * Function to set a generic error as object
 * @param messages list of messages or a single message
 * @param status HTTP response status code
 * @param title Short title for the message
 */
function GenericErrorAsObject(ctx, body, status = 400) {
    ctx.status = status;
    ctx.set('Content-type', 'application/json; charset=UTF-8;');
    ctx.body = body;
}
/**
 * Function to set a file stream response
 * @param ctx Koa context object
 * @param stream Readable stream of the file
 * @param filename Name of the file to be sent
 * @param fileSize Size of the file in bytes
 */
function FileStreamResponse(ctx, stream, filename, fileSize) {
    ctx.status = 200;
    ctx.set('Content-Type', 'application/octet-stream');
    ctx.set('Content-Length', fileSize.toString());
    ctx.attachment(filename); // sets Content-Disposition header to attachment with given filename
    ctx.body = stream;
}
/**
 * Type guard to identify generic HTTP-like error responses.
 *
 * This function checks if the given error object has the minimal shape
 * of an HTTP response — specifically, a numeric `status` field and
 * optionally a `data` object. It is useful for handling errors from
 * non-Axios sources (e.g., mock HTTP clients, fetch wrappers, or internal
 * network utilities) where the thrown object is not an `AxiosError` but
 * still includes `status` and `data`.
 * @param error - The unknown error object to test.
 * @returns True if the object looks like an HTTP response; false otherwise.
 */
function isHttpResponse(error) {
    if (typeof error !== 'object' || error === null) {
        return false;
    }
    const obj = error;
    if (typeof obj.status !== 'number') {
        return false;
    }
    const data = obj.data;
    if (data !== undefined && (typeof data !== 'object' || data === null)) {
        return false;
    }
    return true;
}
//# sourceMappingURL=Index.js.map